

import { useEffect } from "react";
import { useSettings } from "@/context/SettingsContext";
import { getImageUrl } from "@/lib/utils";

export function DynamicManifest() {
  const { settings } = useSettings();
  const { businessName, tagline } = settings.business;
  const { primaryColor } = settings.theme;
  const logoUrl = settings.logoUrl; // Use separate logoUrl from settings
  
  // PWA settings from general settings
  const pwaSettings = settings.pwa || {};
  const pwaName = pwaSettings.appName || businessName || "Shalimar ERP";
  const pwaShortName = pwaSettings.shortName || (businessName ? businessName.split(' ')[0] : "Shalimar");
  const pwaDescription = pwaSettings.description || tagline || "Premium Event Management ERP System";
  // Prefer PWA icons over main logo
  const pwaIcon192 = pwaSettings.icon192 || logoUrl;
  const pwaIcon512 = pwaSettings.icon512 || logoUrl;

  useEffect(() => {
    // Generate dynamic manifest based on current settings
    // Always use /login as start_url for PWA to ensure users go through login
    
    // Use PWA icons from settings, fallback to logo, fallback to empty (browser will handle)
    const icon192Url = pwaIcon192 ? getImageUrl(pwaIcon192) : (logoUrl ? getImageUrl(logoUrl) : '');
    const icon512Url = pwaIcon512 ? getImageUrl(pwaIcon512) : (logoUrl ? getImageUrl(logoUrl) : '');
    
    const manifest = {
      id: "shalimar-erp",
      name: pwaName,
      short_name: pwaShortName,
      description: pwaDescription,
      // Always redirect to login when PWA opens
      start_url: "/login",
      display: "standalone",
      background_color: "#ffffff",
      theme_color: primaryColor || "#C9A84C",
      orientation: "any",
      scope: "/",
      icons: [
        {
          src: icon192Url || '/icon-192x192.png',
          sizes: "192x192",
          type: "image/webp",
          purpose: "any maskable"
        },
        {
          src: icon512Url || '/icon-512x512.png',
          sizes: "512x512",
          type: "image/webp",
          purpose: "any maskable"
        }
      ],
      categories: ["business", "productivity"],
      lang: "en",
      dir: "ltr",
      prefer_related_applications: false,
      shortcuts: [
        {
          name: "Dashboard",
          short_name: "Dashboard",
          description: "View your dashboard",
          url: "/dashboard",
          icons: [{ 
            src: icon192Url || '/icon-192x192.png', 
            sizes: "192x192" 
          }]
        },
        {
          name: "Events",
          short_name: "Events",
          description: "Manage your events",
          url: "/events",
          icons: [{ 
            src: icon192Url || '/icon-192x192.png', 
            sizes: "192x192" 
          }]
        },
        {
          name: "Bookings",
          short_name: "Bookings",
          description: "View bookings calendar",
          url: "/bookings",
          icons: [{ 
            src: icon192Url || '/icon-192x192.png', 
            sizes: "192x192" 
          }]
        }
      ]
    };

    // Create a blob URL for the manifest
    const manifestString = JSON.stringify(manifest, null, 2);
    const blob = new Blob([manifestString], { type: 'application/manifest+json' });
    const manifestUrl = URL.createObjectURL(blob);

    // Find and update the manifest link
    let manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement;
    
    if (!manifestLink) {
      manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      document.head.appendChild(manifestLink);
    }

    // Update the href
    manifestLink.href = manifestUrl;

    // Also update theme-color meta tag
    const themeColorMeta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement;
    if (themeColorMeta) {
      themeColorMeta.content = primaryColor || "#C9A84C";
    }

    // Update the page title
    if (pwaName) {
      document.title = `${pwaName} - ${pwaDescription || 'Premium Event Management'}`;
    }

    // Update apple-mobile-web-app-title
    const appleTitleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]') as HTMLMetaElement;
    if (appleTitleMeta) {
      appleTitleMeta.content = pwaShortName;
    }

    // Update application-name
    const appNameMeta = document.querySelector('meta[name="application-name"]') as HTMLMetaElement;
    if (appNameMeta) {
      appNameMeta.content = pwaShortName;
    }

    return () => {
      // Cleanup: revoke the blob URL
      URL.revokeObjectURL(manifestUrl);
    };
  }, [pwaName, pwaShortName, pwaDescription, primaryColor, pwaIcon192, pwaIcon512]);

  return null;
}
